import { Table, Button, Space, type TableColumnsType, type TableProps } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { FilePdfOutlined } from '@ant-design/icons';
import { ExportExcelButton, ColumnManagerDropdown } from '../../../common';
import { useColumnManager } from '../../../../hooks';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const PaginatedTableDetalleProduccion = ({ csvName, getInfoAgregada }: any) => {

    // Fetch Data
    const [fullTableData, setFullTableData] = useState([]);
    const [filteredTableData, setFilteredTableData] = useState(fullTableData)
    const [loading, setLoading] = useState(false)
    const [rindeFibra, setRindeFibra] = useState<number>(0)
    const [metricas, setMetricas] = useState<any>({})

    const [queryParameters] = useSearchParams()
    const nro_produccion = queryParameters.get("nro_produccion")

    const {user, getAccessTokenSilently} = useAuth0();
    
    const id_empresa = user?.id_empresa
    const id_cliente = user?.id_cliente 
    const tipo_usuario = user?.tipo_usuario
  
    const variables_api = `${tipo_usuario}/${id_empresa}/${id_cliente}`

    // Para cuando se filtre la tabla desde la tabla.
    // En lugar de modificar la tabla completa, usamos otra variable.
    const onChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter, extra: any) => {
        setFilteredTableData(extra.currentDataSource);
      };

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoading(true);
          const apiEndpoint = `${process.env.REACT_APP_REPORTES_URL}/produccion/detalle/${variables_api}/${nro_produccion}`;
          const token = await getAccessTokenSilently();
          const response = await fetch(apiEndpoint, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          const data = await response.json();
          // Compartimos y guardamos info.
          setFullTableData(Array.isArray(data?.data) ? data.data : []);
          const met = data?.metricas || {};
          getInfoAgregada(met);
          setMetricas(met);
          setRindeFibra(parseFloat(met?.rinde_fibra) || 0);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching data:', error);
          setLoading(false);
        }
      };
      if (nro_produccion && variables_api) {
        fetchData();
      }
    }, [nro_produccion, variables_api, getAccessTokenSilently]);

    // Table
    type DataType = {
        nro_fardo : number
        kilos_fardo : number
        kilos_tara : number
        kilos_fibra : number
        fecha_hora : Date
        rinde_fibra : number
      }
      
    const allColumns: TableColumnsType<DataType> = [
      {
        title: 'N° Fardo',
        dataIndex: 'nro_fardo',
        key: 'nro_fardo',
        width: 120,
        align: 'center',
        render: (text) => <strong>{text}</strong>,
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.nro_fardo - b.nro_fardo,
      },
      {
        title: 'Kg. Total',
        dataIndex: 'kilos_fardo',
        key: 'kilos_fardo',
        width: 130,
        align: 'right',
        render: (value) => new Intl.NumberFormat('es-AR', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        }).format(value),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.kilos_fardo - b.kilos_fardo,
      },
      {
        title: 'Kg. Tara',
        dataIndex: 'kilos_tara',
        key: 'kilos_tara',
        width: 130,
        align: 'right',
        render: (value) => new Intl.NumberFormat('es-AR', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        }).format(value),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.kilos_tara - b.kilos_tara,
      },
      {
        title: 'Kg. Fibra',
        key: 'kilos_fibra',
        width: 130,
        align: 'right',
        render: (_: any, record: DataType) => {
          const fibra = record.kilos_fardo - record.kilos_tara;
          return new Intl.NumberFormat('es-AR', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          }).format(fibra);
        },
  
        sorter: (a, b) => (a.kilos_fardo - a.kilos_tara) - (b.kilos_fardo - b.kilos_tara),
      },
      {
        title: 'Fecha',
        dataIndex: 'fecha_hora',
        key: 'fecha_hora',
        width: 180,
  
        render: (date: Date) => {
          if (!date) return '-';
          const d = new Date(date);
          return d.toLocaleString('es-AR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        },
        sorter: (a, b) => new Date(a.fecha_hora).valueOf() - new Date(b.fecha_hora).valueOf(),
      },
      {
        title: 'Rinde Fibra %',
        dataIndex: 'rinde_fibra',
        key: 'rinde_fibra',
  
        align: 'right',
        render: (text: number) => `${text}%`,
      },
    ]

    // Lista de todas las columnas disponibles (keys)
    const allColumnKeys = allColumns.map(col => col.key as string).filter(Boolean);

    // Nombres amigables para las columnas
    const columnNames: Record<string, string> = {
        'nro_fardo': 'N° Fardo',
        'kilos_fardo': 'Kg. Total',
        'kilos_tara': 'Kg. Tara',
        'kilos_fibra': 'Kg. Fibra',
        'fecha_hora': 'Fecha',
        'rinde_fibra': 'Rinde Fibra %'
    };

    // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
    const columnManager = useColumnManager({
        storageKey: 'detalle_produccion',
        allKeys: allColumnKeys,
    });

    // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
    const filteredColumns = useMemo(() => {
        const columnsByKey = new Map(allColumns.map(col => [col.key as string, col]));
        return columnManager.orderedVisibleKeys
            .map(key => columnsByKey.get(key))
            .filter((col): col is (typeof allColumns)[number] => Boolean(col));
    }, [columnManager.orderedVisibleKeys, allColumns]);

    const columns: TableColumnsType<DataType> = filteredColumns;

    const data: DataType[] = fullTableData.map((fila: any) => {
        return {
            nro_fardo : fila.nro_fardo,
            kilos_fardo : fila.kilos_fardo,
            kilos_tara : fila.kilos_tara,
            kilos_fibra : fila.kilos_fardo - fila.kilos_tara,
            fecha_hora : fila.fecha_hora,
            rinde_fibra : rindeFibra
        }
    });

    const fmt = (v: number | undefined) =>
        new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v ?? 0);

    // Función para exportar a PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const leftMargin = 14;
        const lineHeight = 7;
        let y = 18;

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Detalle de Producción N° ${nro_produccion || ''}`, pageWidth / 2, y, { align: 'center' });
        y += lineHeight * 1.5;

        // Resumen
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('RESUMEN', leftMargin, y);
        y += lineHeight * 0.6;
        doc.setLineWidth(0.3);
        doc.line(leftMargin, y, pageWidth - leftMargin, y);
        y += lineHeight * 0.8;

        const summaryRows = [
            ['Cliente', metricas.cliente || '-', 'Productor', metricas.productor || '-'],
            ['Bruto Fardos', fmt(metricas.kg_total), 'Tara Fardos', fmt(metricas.kg_tara)],
            ['Fibra', fmt((metricas.kg_total ?? 0) - (metricas.kg_tara ?? 0)), 'Kg Romaneo Bruto', fmt(metricas.kg_romaneo_bruto)],
            ['Rinde Fibra', metricas.rinde_fibra !== undefined ? `${metricas.rinde_fibra.toFixed(2)}%` : '0%', 'Cantidad Fardos', String(metricas.cantidad_fardos ?? 0)],
            ['Resto (+)', fmt(metricas.resto_positivo), 'Resto (-)', fmt(metricas.resto_negativo)],
        ];

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        summaryRows.forEach(([lbl1, val1, lbl2, val2]) => {
            doc.setFont('helvetica', 'bold');
            doc.text(`${lbl1}:`, leftMargin, y);
            doc.setFont('helvetica', 'normal');
            doc.text(val1, leftMargin + 32, y);
            if (lbl2) {
                doc.setFont('helvetica', 'bold');
                doc.text(`${lbl2}:`, pageWidth / 2, y);
                doc.setFont('helvetica', 'normal');
                doc.text(val2, pageWidth / 2 + 35, y);
            }
            y += lineHeight;
        });
        y += lineHeight * 0.5;

        // Tabla de fardos
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('DETALLE DE FARDOS', leftMargin, y);
        y += lineHeight * 0.3;

        const tableRows = data.map((row: DataType) => [
            String(row.nro_fardo),
            fmt(row.kilos_fardo),
            fmt(row.kilos_tara),
            fmt(row.kilos_fibra),
            row.fecha_hora ? new Date(row.fecha_hora).toLocaleString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-',
            `${rindeFibra}%`,
        ]);

        autoTable(doc, {
            startY: y,
            head: [['N° Fardo', 'Kg Total', 'Kg Tara', 'Kg Fibra', 'Fecha', 'Rinde %']],
            body: tableRows,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [45, 85, 140], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [245, 247, 250] },
            margin: { left: leftMargin, right: leftMargin },
            columnStyles: {
                0: { halign: 'center' },
                1: { halign: 'right' },
                2: { halign: 'right' },
                3: { halign: 'right' },
                4: { halign: 'center' },
                5: { halign: 'right' },
            },
        });

        doc.save(`detalle_produccion_${nro_produccion || 'reporte'}.pdf`);
    };

    // Función para exportar a Excel
    const handleExportExcel = () => {
        try {
            const sourceData: any[] = filteredTableData.length > 0 ? filteredTableData : data;
            const exportData = sourceData.map((row: any) => {
                const exportRow: Record<string, any> = {};
                columnManager.orderedVisibleKeys.forEach(key => {
                    exportRow[columnNames[key] || key] = row[key];
                });
                return exportRow;
            });
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Detalle Producción');
            XLSX.writeFile(workbook, csvName || `detalle_produccion_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error('Error al exportar Excel:', error);
        }
    };

    return (
      <div>
        <div style={{ 
            marginBottom: '16px', 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap'
        }}>
            <ColumnManagerDropdown
                manager={columnManager}
                labels={columnNames}
                buttonSize="middle"
            />
            <Button
                icon={<FilePdfOutlined />}
                size="middle"
                onClick={handleExportPDF}
                disabled={fullTableData.length === 0}
                style={{ backgroundColor: '#e74c3c', borderColor: '#e74c3c', color: '#fff' }}
            >
                PDF
            </Button>
            <ExportExcelButton
                onClick={handleExportExcel}
                size="middle"
                disabled={fullTableData.length === 0}
            />
        </div>
        <Table 
            columns={columns} 
            dataSource={data}
            bordered
            size="middle"
            scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 500px))' }}
            onChange={onChange}
            loading={loading}
            pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                pageSizeOptions: ['10', '25', '50', '100'],
                position: ['bottomRight']
            }}
        />
      </div>
    );
  };
  
  export default PaginatedTableDetalleProduccion;